import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ClientPrivacyControls from "@/components/ClientPrivacyControls";
import { CONFIGURABLE_PAGES, ALL_SLUGS } from "@/lib/page-permissions";
import {
  ChevronLeft, ShieldCheck, Send, RefreshCw, Trash2, Check,
} from "lucide-react";

const INVITE_STATUS_STYLES = {
  sent:             { bg: "#FEF9C3", color: "#854D0E", label: "Invite sent" },
  delivered:        { bg: "#D1FAE5", color: "#065F46", label: "Delivered" },
  opened:           { bg: "#D1FAE5", color: "#065F46", label: "Opened" },
  clicked:          { bg: "#D1FAE5", color: "#065F46", label: "Clicked" },
  delivery_delayed: { bg: "#FEF9C3", color: "#854D0E", label: "Delayed" },
  bounced:          { bg: "#FEE2E2", color: "#991B1B", label: "Bounced" },
  complained:       { bg: "#FEE2E2", color: "#991B1B", label: "Complained" },
};

function InviteStatusBadge({ user }) {
  if (user.active) {
    return (
      <span className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1 rounded-full"
            style={{ background: "#D1FAE5", color: "#065F46" }}>
        Active
      </span>
    );
  }
  if (user.invite_email_status) {
    const s = INVITE_STATUS_STYLES[user.invite_email_status];
    if (s) {
      return (
        <span className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1 rounded-full"
              style={{ background: s.bg, color: s.color }}>
          {s.label}
        </span>
      );
    }
  }
  if (user.invite_resend_id) {
    return (
      <span className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1 rounded-full"
            style={{ background: "#FEF9C3", color: "#854D0E" }}>
        Invite sent
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1 rounded-full"
          style={{ background: "var(--fb-border)", color: "var(--fb-text-muted)" }}>
      Invite pending
    </span>
  );
}

export default function StaffEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: me } = useAuth();

  const [staffUser, setStaffUser] = useState(null);
  const [name, setName] = useState("");
  const [role, setRole] = useState("staff");
  const [pagePerms, setPagePerms] = useState(ALL_SLUGS);
  const [hiddenFields, setHiddenFields] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resending, setResending] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const [inviteError, setInviteError] = useState("");

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get(`/staff/${id}`);
      setStaffUser(data);
      setName(data.name || "");
      setRole(data.role || "staff");
      setHiddenFields(data.hidden_client_fields || []);
      setPagePerms(
        data.page_permissions !== null && data.page_permissions !== undefined
          ? data.page_permissions
          : ALL_SLUGS
      );
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [id]);

  function handleRoleChange(newRole) {
    setRole(newRole);
    if (newRole === "admin") setPagePerms(ALL_SLUGS);
  }

  function togglePage(slug) {
    setPagePerms(prev =>
      prev.includes(slug) ? prev.filter(s => s !== slug) : [...prev, slug]
    );
  }

  async function save() {
    setSaving(true); setError(""); setSaved(false);
    try {
      await api.put(`/staff/${id}`, { name, role, hidden_client_fields: hiddenFields });
      if (role !== "admin") {
        const allChecked = ALL_SLUGS.every(s => pagePerms.includes(s));
        await api.put(`/staff/${id}/page-permissions`, {
          page_permissions: allChecked ? null : pagePerms,
        });
      }
      setSaved(true);
      await load();
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSaving(false);
    }
  }

  async function resendInvite() {
    setResending(true); setInviteError("");
    try {
      await api.post(`/staff/${id}/invite`);
      await load();
    } catch (e) {
      setInviteError(formatApiError(e));
    } finally {
      setResending(false);
    }
  }

  async function deleteStaff() {
    if (!window.confirm(`Permanently delete ${staffUser?.email}? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await api.delete(`/staff/${id}`);
      navigate("/staff/users");
    } catch (e) {
      setError(formatApiError(e));
      setDeleting(false);
    }
  }

  const isSelf = me?.id === id;

  if (loading) {
    return (
      <div className="text-sm" style={{ color: "var(--fb-text-muted)" }}>Loading…</div>
    );
  }

  if (!staffUser && error) {
    return (
      <div className="fb-status-error text-sm p-3 rounded-lg">{error}</div>
    );
  }

  const displayName = staffUser?.name || staffUser?.email || "Staff member";

  return (
    <div className="space-y-6 max-w-2xl" data-testid="staff-edit-page">
      <div>
        <button
          onClick={() => navigate("/staff/users")}
          className="inline-flex items-center gap-1 text-sm font-semibold mb-4 hover:opacity-70 transition-opacity"
          style={{ color: "var(--fb-primary)" }}
        >
          <ChevronLeft className="w-4 h-4" /> Staff Logins
        </button>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold mb-2" style={{ color: "#8B918B" }}>Administration</div>
        <h1 className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: "Outfit" }}>
          {displayName}
        </h1>
        {isSelf && (
          <span className="inline-block mt-2 text-xs font-semibold px-2 py-0.5 rounded-full"
                style={{ background: "var(--fb-bg)", color: "var(--fb-text-muted)", border: "1px solid var(--fb-border)" }}>
            This is you
          </span>
        )}
      </div>

      {error && <div className="fb-status-error text-sm p-3 rounded-lg">{error}</div>}

      {/* Profile */}
      <div className="fb-card p-5 sm:p-6 space-y-4">
        <h2 className="text-base font-semibold" style={{ fontFamily: "Outfit" }}>Profile</h2>
        <div>
          <label className="block text-sm font-medium mb-1.5" style={{ color: "var(--fb-text-muted)" }}>Email</label>
          <div className="px-3.5 py-2.5 rounded-lg text-sm"
               style={{ background: "var(--fb-bg)", border: "1px solid var(--fb-border)", color: "var(--fb-text-muted)" }}>
            {staffUser.email}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1.5">Name</label>
          <input
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="Full name"
            className="w-full px-3.5 py-2.5 rounded-lg border outline-none focus:border-[#052F66] text-sm"
            style={{ borderColor: "var(--fb-border)", background: "#FAFAF8" }}
            data-testid="edit-staff-name"
          />
        </div>
      </div>

      {/* Access */}
      <div className="fb-card p-5 sm:p-6 space-y-4">
        <h2 className="text-base font-semibold" style={{ fontFamily: "Outfit" }}>Access</h2>
        <div>
          <label className="block text-sm font-medium mb-1.5">Role</label>
          <select
            value={role}
            onChange={e => handleRoleChange(e.target.value)}
            className="w-full px-3.5 py-2.5 rounded-lg border bg-white text-sm"
            style={{ borderColor: "var(--fb-border)" }}
            data-testid="edit-staff-role"
          >
            <option value="viewer">Viewer — read-only access</option>
            <option value="staff">Staff — read/write access</option>
            <option value="admin">Admin — full access + manage staff</option>
          </select>
        </div>

        {role !== "admin" && (
          <div>
            <label className="block text-sm font-medium mb-3">Page access</label>
            <div className="grid grid-cols-2 gap-2">
              {CONFIGURABLE_PAGES.map(({ slug, label }) => (
                <label
                  key={slug}
                  className="flex items-center gap-2.5 text-sm cursor-pointer select-none py-1"
                  data-testid={`page-perm-${slug}`}
                >
                  <input
                    type="checkbox"
                    checked={pagePerms.includes(slug)}
                    onChange={() => togglePage(slug)}
                    className="rounded"
                  />
                  <span style={{ color: "var(--fb-text)" }}>{label}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {role === "admin" && (
          <div className="flex items-center gap-2 text-sm" style={{ color: "var(--fb-text-muted)" }}>
            <ShieldCheck className="w-4 h-4" style={{ color: "var(--fb-primary)" }} />
            Admins always have access to all pages.
          </div>
        )}
        <div className="pt-4 border-t" style={{ borderColor: "var(--fb-border)" }}>
          <ClientPrivacyControls value={hiddenFields} onChange={setHiddenFields} />
        </div>
      </div>

      {/* Invite */}
      {!staffUser.active && (
        <div className="fb-card p-5 sm:p-6 space-y-4">
          <h2 className="text-base font-semibold" style={{ fontFamily: "Outfit" }}>Invitation</h2>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-sm mb-1.5" style={{ color: "var(--fb-text-muted)" }}>
                This account hasn't been activated yet.
              </div>
              <InviteStatusBadge user={staffUser} />
            </div>
            <button
              onClick={resendInvite}
              disabled={resending}
              className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg border hover:bg-stone-50 disabled:opacity-50"
              style={{ borderColor: "var(--fb-border)" }}
              data-testid="resend-invite-btn"
            >
              {resending ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {resending ? "Sending…" : "Resend invite"}
            </button>
          </div>
          {inviteError && <div className="fb-status-error text-sm p-3 rounded-lg">{inviteError}</div>}
        </div>
      )}

      {/* Save / Cancel */}
      <div className="flex justify-end gap-3">
        <button
          onClick={() => navigate("/staff/users")}
          className="px-5 py-2.5 rounded-lg border text-sm font-semibold"
          style={{ borderColor: "var(--fb-border)" }}
        >
          Cancel
        </button>
        <button
          onClick={save}
          disabled={saving}
          className="fb-btn-primary px-5 py-2.5 rounded-lg font-semibold text-sm inline-flex items-center gap-2 disabled:opacity-60"
          data-testid="save-staff-btn"
        >
          {saved
            ? <><Check className="w-4 h-4" /> Saved</>
            : saving ? "Saving…" : "Save changes"}
        </button>
      </div>

      {/* Danger zone */}
      {!isSelf && (
        <div className="fb-card p-5 sm:p-6"
             style={{ borderColor: "var(--fb-error)", borderWidth: 1 }}>
          <h2 className="text-base font-semibold mb-1" style={{ color: "var(--fb-error)", fontFamily: "Outfit" }}>
            Danger Zone
          </h2>
          <p className="text-sm mb-4" style={{ color: "var(--fb-text-muted)" }}>
            Permanently delete this staff account. This cannot be undone.
          </p>
          <button
            onClick={deleteStaff}
            disabled={deleting}
            className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg disabled:opacity-50"
            style={{ background: "#FEF2F2", color: "var(--fb-error)", border: "1px solid #FECACA" }}
            data-testid="delete-staff-btn"
          >
            <Trash2 className="w-4 h-4" />
            {deleting ? "Deleting…" : "Delete staff member"}
          </button>
        </div>
      )}
    </div>
  );
}
